// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Header,
  Button,
  Table,
  Icon,
  Message,
  Loader,
  Input,
  Label,
  Checkbox,
  Pagination,
  Dropdown
} from 'semantic-ui-react';
import { providerApi, Provider } from '../api/providerApi';
import PolicyTypeBrokerageModal from '../components/PolicyTypeBrokerageModal';

function ProviderListPage() {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactiveOnly, setShowInactiveOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const navigate = useNavigate();

  const loadProviders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await providerApi.getAll({
        search: searchTerm || undefined,
        isActive: showInactiveOnly ? false : undefined,
        page: currentPage,
        size: pageSize
      });
      setProviders(data.content);
      setTotalPages(data.totalPages);
      setTotalElements(data.totalElements);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load providers');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, showInactiveOnly, currentPage, pageSize]);

  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm, showInactiveOnly]);

  const handleDelete = async (id: string) => {
    if (!globalThis.confirm('Are you sure you want to delete this provider? This will soft-delete it.')) {
      return;
    }

    try {
      await providerApi.delete(id);
      await loadProviders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete provider');
    }
  };

  return (
    <Container style={{ marginTop: '2rem' }}>
      <Header as='h1'>
        <Icon name='factory' />
        <Header.Content>
          Provider Management
          <Header.Subheader>Manage financial service providers</Header.Subheader>
        </Header.Content>
      </Header>

      {/* Search and Filter Bar */}
      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'center' }}>
        <Input
          icon='search'
          placeholder='Search providers...'
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ flex: 1 }}
        />
        <Checkbox
          label='Show inactive only'
          checked={showInactiveOnly}
          onChange={(_e, data) => setShowInactiveOnly(!!data.checked)}
        />
        <Button primary onClick={() => navigate('/providers/new')}>
          <Icon name='plus' />
          New Provider
        </Button>
      </div>

      {error && (
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {loading ? (
        <Loader active inline='centered'>
          Loading providers...
        </Loader>
      ) : (
        <>
          <Table celled striped>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Name</Table.HeaderCell>
                <Table.HeaderCell>Code</Table.HeaderCell>
                <Table.HeaderCell>Description</Table.HeaderCell>
                <Table.HeaderCell>Contact</Table.HeaderCell>
                <Table.HeaderCell>LOA Notifications</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>Actions</Table.HeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {providers.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan='7' textAlign='center'>
                    No providers found
                  </Table.Cell>
                </Table.Row>
              ) : (
                providers.map((provider) => (
                  <Table.Row key={provider.id}>
                    <Table.Cell>
                      <Header as='h4'>
                        {provider.name}
                        {provider.websiteUrl && (
                          <a href={provider.websiteUrl} target='_blank' rel='noopener noreferrer' style={{ marginLeft: '8px' }}>
                            <Icon name='external' size='small' />
                          </a>
                        )}
                      </Header>
                    </Table.Cell>
                    <Table.Cell><code>{provider.code}</code></Table.Cell>
                    <Table.Cell>{provider.description || '-'}</Table.Cell>
                    <Table.Cell>
                      {provider.contactEmail && (
                        <div><Icon name='mail' /> {provider.contactEmail}</div>
                      )}
                      {provider.contactPhone && (
                        <div><Icon name='phone' /> {provider.contactPhone}</div>
                      )}
                      {!provider.contactEmail && !provider.contactPhone && '-'}
                    </Table.Cell>
                    <Table.Cell>
                      {provider.email && (
                        <div><Icon name='mail outline' /> {provider.email}</div>
                      )}
                      {provider.contactName && (
                        <div><Icon name='user' /> {provider.contactName}</div>
                      )}
                      {!provider.email && !provider.contactName && '-'}
                    </Table.Cell>
                    <Table.Cell>
                      <Label color={provider.isActive ? 'green' : 'red'}>
                        {provider.isActive ? 'Active' : 'Inactive'}
                      </Label>
                    </Table.Cell>
                    <Table.Cell>
                      <Button.Group size='small'>
                        <Button
                          icon='edit'
                          color='blue'
                          onClick={() => navigate(`/providers/${provider.id}/edit`)}
                          title='Edit provider'
                        />
                        <Button
                          icon='file alternate'
                          color='teal'
                          onClick={() => {
                            setSelectedProvider(provider);
                            setModalOpen(true);
                          }}
                          title='Manage policy types'
                        />
                        <Button
                          icon='trash'
                          color='red'
                          onClick={() => handleDelete(provider.id!)}
                          title='Delete provider'
                        />
                      </Button.Group>
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table>

          <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ color: '#666' }}>
              Showing {currentPage * pageSize + 1}-{Math.min((currentPage + 1) * pageSize, totalElements)} of {totalElements} provider{totalElements === 1 ? '' : 's'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span>Items per page:</span>
              <Dropdown
                compact
                selection
                value={pageSize}
                options={[
                  { key: 10, text: '10', value: 10 },
                  { key: 20, text: '20', value: 20 },
                  { key: 50, text: '50', value: 50 },
                  { key: 100, text: '100', value: 100 }
                ]}
                onChange={(_e, data) => {
                  setPageSize(data.value as number);
                  setCurrentPage(0); // Reset to first page when changing page size
                }}
              />
              <Pagination
                activePage={currentPage + 1}
                totalPages={totalPages}
                onPageChange={(_e, data) => setCurrentPage((data.activePage as number) - 1)}
                siblingRange={1}
                boundaryRange={0}
                ellipsisItem={null}
                firstItem={null}
                lastItem={null}
                prevItem={{ content: <Icon name='chevron left' />, icon: true }}
                nextItem={{ content: <Icon name='chevron right' />, icon: true }}
              />
            </div>
          </div>
        </>
      )}

      {selectedProvider && (
        <PolicyTypeBrokerageModal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setSelectedProvider(null);
          }}
          providerId={selectedProvider.id!}
          providerName={selectedProvider.name}
        />
      )}
    </Container>
  );
}

export default ProviderListPage;
